import { useEffect } from "react";
import { useMutation, useQueries, useQuery, UseQueryResult } from "react-query";
import { AxiosError } from "axios";
import HttpService from "../../services/http";
import { queryClient } from "../../providers/QueryProvider";

export type WeekdayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const WEEKDAY_KEYS: WeekdayKey[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

export type TimeRange = [string, string];

export interface AgendaNotifications {
  confirmation: boolean;
  email_reminder_hours: number | null;
  member_reminder: boolean;
  sms_reminder: boolean;
}

export interface AgendaMember {
  user_identification: string;
  name: string;
}

/** `public_name` is what clients see: the username, else the first name. */
export interface AgendaTypeMember extends AgendaMember {
  public_name: string | null;
}

/** The languages the server writes the agenda's e-mails in. */
export const AGENDA_EMAIL_LANGUAGES = [
  "en",
  "fr",
  "de",
  "es",
  "it",
  "pt",
  "ar",
  "ja",
  "ru",
  "zh",
] as const;

export type AgendaEmailLanguage = (typeof AGENDA_EMAIL_LANGUAGES)[number];

export interface AgendaConfig {
  enabled: boolean;
  timezone: string;
  notifications: AgendaNotifications;
  /** `null`: the person booked gets the e-mails in the language of the client. */
  member_language: AgendaEmailLanguage | null;
  members: AgendaMember[];
}

export type AgendaConfigInput = Pick<AgendaConfig, "enabled" | "timezone"> & {
  notifications?: Partial<AgendaNotifications>;
  member_language?: AgendaEmailLanguage | null;
};

export interface MemberSettings {
  tz: string;
  hours: Partial<Record<WeekdayKey, TimeRange[]>>;
}

/** `name` is null for a person UMD no longer has. */
export interface AppointmentParticipant {
  user_identification: string;
  name: string | null;
}

export type PaymentCurrency = "EUR" | "USD" | "CHF";

/** `due`: booked, still to pay; `paid`: received. */
export type PaymentStatus = "due" | "paid";

/** Only the status remains once the copy holding the whole record is gone with its owner. */
export interface AppointmentPayment {
  status: PaymentStatus;
  amount?: number;
  currency?: PaymentCurrency;
  link: string | null;
  /** The client who pays, set at booking. */
  payer?: string;
}

export interface AppointmentSummary {
  id: string;
  day: string;
  start: number;
  end: number;
  title: string;
  organizer: string;
  participants: AppointmentParticipant[];
  /** `pending`: held until `hold_expires_at` while the client pays. */
  status: string;
  source: string;
  payment?: Pick<AppointmentPayment, "status"> | null;
  hold_expires_at?: number | null;
}

export interface Appointment extends AppointmentSummary {
  timezone: string;
  location: string;
  description: string;
  form_id: string | null;
  type_id: string | null;
  payment?: AppointmentPayment | null;
}

/** A payment that arrived after its slot was given to someone else. */
export interface PaymentIssue {
  id: string;
  at: number;
  start: number;
  timezone: string;
  title: string;
  member: string;
  amount: number;
  currency: PaymentCurrency;
  day: string;
  /** The payment's link code, as in the client's payment history. */
  code?: string | null;
  /** `taken`: the slot went to someone else; `cancelled`: paid after its deletion; `duplicate`: paid twice. */
  reason?: "taken" | "cancelled" | "duplicate";
}

export interface AppointmentInput {
  start: number;
  end: number;
  timezone: string;
  title: string;
  location: string;
  description: string;
  participants: string[];
  lang: string;
}

export interface UserAppointments {
  appointments: Appointment[];
  next_start: number | null;
  last_start: number | null;
  total: number;
  payment_issues: PaymentIssue[];
}

export interface AgendaRange {
  from: number;
  to: number;
}

/** `required`: no booking without payment; `accepted`: booked, to pay. Amounts are in cents. */
export interface TypePayment {
  amount: number;
  currency: PaymentCurrency;
  unpaid: "required" | "accepted";
}

export interface AgendaType {
  id: string;
  title: string;
  members: AgendaTypeMember[];
  duration_min: number;
  min_notice_hours: number;
  horizon_days: number;
  max_upcoming_per_client: number | null;
  signup_form_id: string | null;
  active: boolean;
  /** Shown on the entity's booking page; an unlisted type stays bookable through its own link. */
  listed: boolean;
  description: string;
  location: string;
  image_url: string | null;
  payment: TypePayment | null;
}

export interface AgendaTypeInput {
  title: string;
  members: string[];
  duration_min: number;
  min_notice_hours: number;
  horizon_days: number;
  max_upcoming_per_client: number | null;
  signup_form_id: string | null;
  active?: boolean;
  listed?: boolean;
  description?: string;
  location?: string;
  image_url?: string | null;
  payment?: TypePayment | null;
}

const base = (customerId: string, entityId: string) =>
  `/${customerId}/${entityId}/agenda`;

const configKey = (entityId: string) => ["agenda-config", entityId];
const memberSettingsKey = (entityId: string, userId: string) => [
  "agenda-member-settings",
  entityId,
  userId,
];
const appointmentsKey = (entityId: string) => ["agenda-appointments", entityId];
const typesKey = (entityId: string) => ["agenda-types", entityId];

const detailKey = (entityId: string, id: string) => [
  ...appointmentsKey(entityId),
  "detail",
  id,
];

// A moved or deleted appointment answers 404 at its former day: its detail is dropped, and read
// again by whatever still shows it, which AppointmentModal holds back while it writes.
const invalidateAppointments = (entityId: string, changedId?: string) => {
  if (changedId) queryClient.removeQueries(detailKey(entityId, changedId));
  return queryClient.invalidateQueries(appointmentsKey(entityId), {
    predicate: (query) => query.queryKey[2] !== "detail",
  });
};

/** A change that failed left the appointment as it was, unless the answer says it is gone. */
const afterFailedChange = (
  entityId: string,
  id: string,
  error: AxiosError<any>
) =>
  invalidateAppointments(
    entityId,
    error.response?.status === 404 ? id : undefined
  );

const LIST_QUERY_OPTIONS = {
  staleTime: 30_000,
  keepPreviousData: true,
} as const;

export const useGetAgendaConfig = (
  customerId: string,
  entityId: string,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery<AgendaConfig, AxiosError<any>>(
    configKey(entityId),
    async () => {
      const res = await HttpService.get(`${base(customerId, entityId)}/config`);
      return res?.data?.element;
    },
    { enabled: enabled && !!customerId && !!entityId, staleTime: 60_000 }
  );

export const useUpdateAgendaConfig = (customerId: string, entityId: string) =>
  useMutation<AgendaConfig, AxiosError<any>, AgendaConfigInput>(
    async (input) => {
      const res = await HttpService.put(
        `${base(customerId, entityId)}/config`,
        input
      );
      return res?.data?.element;
    },
    {
      onSuccess: (config) => {
        if (config) queryClient.setQueryData(configKey(entityId), config);
        else queryClient.invalidateQueries(configKey(entityId));
      },
    }
  );

interface SettingsQueryOptions {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean | "always";
}

/** `null`: the person has no availability, so is bookable at any time once a type offers them. */
const memberSettingsQuery = (
  customerId: string,
  entityId: string,
  userId: string,
  { enabled = true, refetchOnWindowFocus = true }: SettingsQueryOptions
) => ({
  queryKey: memberSettingsKey(entityId, userId),
  queryFn: async (): Promise<MemberSettings | null> => {
    const res = await HttpService.get(
      `${base(customerId, entityId)}/members/${userId}/settings`
    );
    return res?.data?.element ?? null;
  },
  enabled: enabled && !!customerId && !!entityId && !!userId,
  staleTime: 60_000,
  refetchOnWindowFocus,
});

export const useGetMemberSettings = (
  customerId: string,
  entityId: string,
  userId: string,
  options: SettingsQueryOptions = {}
) =>
  useQuery<MemberSettings | null, AxiosError<any>>(
    memberSettingsQuery(customerId, entityId, userId, options)
  );

export const useGetMembersSettings = (
  customerId: string,
  entityId: string,
  userIds: string[],
  options: SettingsQueryOptions = {}
) =>
  useQueries(
    userIds.map((userId) =>
      memberSettingsQuery(customerId, entityId, userId, options)
    )
  ) as UseQueryResult<MemberSettings | null, AxiosError<any>>[];

export const useUpdateMemberSettings = (customerId: string, entityId: string) =>
  useMutation<
    MemberSettings,
    AxiosError<any>,
    { userId: string; settings: MemberSettings }
  >(
    async ({ userId, settings }) => {
      const res = await HttpService.put(
        `${base(customerId, entityId)}/members/${userId}/settings`,
        settings
      );
      return res?.data?.element;
    },
    {
      onSuccess: (_settings, { userId }) =>
        queryClient.invalidateQueries(memberSettingsKey(entityId, userId)),
    }
  );

export const useDeleteMemberSettings = (customerId: string, entityId: string) =>
  useMutation<null, AxiosError<any>, string>(
    async (userId) => {
      await HttpService.delete(
        `${base(customerId, entityId)}/members/${userId}/settings`
      );
      return null;
    },
    {
      onSuccess: (_result, userId) => {
        queryClient.setQueryData(memberSettingsKey(entityId, userId), null);
        return queryClient.invalidateQueries(
          memberSettingsKey(entityId, userId)
        );
      },
    }
  );

export const useGetAgendaOverview = (
  customerId: string,
  entityId: string,
  {
    from,
    to,
    members,
    person,
  }: AgendaRange & { members?: string[]; person?: string },
  {
    enabled = true,
    keepPreviousData = true,
  }: { enabled?: boolean; keepPreviousData?: boolean } = {}
) => {
  const memberList = (members ?? []).join(",");
  return useQuery<AppointmentSummary[], AxiosError<any>>(
    [
      ...appointmentsKey(entityId),
      "overview",
      from,
      to,
      memberList,
      person ?? "",
    ],
    async () => {
      const res = await HttpService.get(
        `${base(customerId, entityId)}/overview`,
        {
          params: {
            from,
            to,
            ...(memberList ? { members: memberList } : {}),
            ...(person ? { person } : {}),
          },
        }
      );
      return res?.data?.element?.appointments ?? [];
    },
    {
      enabled: enabled && !!customerId && !!entityId,
      ...LIST_QUERY_OPTIONS,
      keepPreviousData,
    }
  );
};

export const useGetMemberAppointments = (
  customerId: string,
  entityId: string,
  userId: string,
  { from, to }: AgendaRange,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery<AppointmentSummary[], AxiosError<any>>(
    [...appointmentsKey(entityId), "member", userId, from, to],
    async () => {
      const res = await HttpService.get(
        `${base(customerId, entityId)}/members/${userId}/appointments`,
        { params: { from, to } }
      );
      return res?.data?.element?.appointments ?? [];
    },
    {
      enabled: enabled && !!customerId && !!entityId && !!userId,
      ...LIST_QUERY_OPTIONS,
    }
  );

export const useGetUserAppointments = (
  customerId: string,
  entityId: string,
  userId: string,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery<UserAppointments, AxiosError<any>>(
    [...appointmentsKey(entityId), "user", userId],
    async () => {
      const res = await HttpService.get(
        `${base(customerId, entityId)}/users/${userId}/appointments`
      );
      const element = res?.data?.element ?? {};
      return {
        appointments: element.appointments ?? [],
        next_start: element.next_start ?? null,
        last_start: element.last_start ?? null,
        total: element.total ?? 0,
        payment_issues: element.payment_issues ?? [],
      };
    },
    {
      enabled: enabled && !!customerId && !!entityId && !!userId,
      ...LIST_QUERY_OPTIONS,
    }
  );

export const useGetAppointment = (
  customerId: string,
  entityId: string,
  id: string,
  day: string,
  participant: string,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery<Appointment, AxiosError<any>>(
    [...detailKey(entityId, id), day],
    async () => {
      const res = await HttpService.get(
        `${base(customerId, entityId)}/appointments/${id}`,
        { params: { day, ...(participant ? { participant } : {}) } }
      );
      return res?.data?.element;
    },
    {
      enabled: enabled && !!customerId && !!entityId && !!id && !!day,
      retry: (failures, error) =>
        error?.response?.status !== 404 && failures < 3,
    }
  );

/**
 * Creates the payment link of an appointment still to pay that has none; the answer carries it. NOT_DUE means it
 * was paid or changed meanwhile: as after a link, the appointment is read again.
 */
export const useCreatePaymentLink = (customerId: string, entityId: string) =>
  useMutation<
    Appointment,
    AxiosError<any>,
    { id: string; participant?: string | null }
  >(
    async ({ id, participant }) => {
      const res = await HttpService.post(
        `${base(customerId, entityId)}/appointments/${id}/payment-link`,
        {},
        { params: participant ? { participant } : {} }
      );
      return res?.data?.element;
    },
    {
      onSettled: (_answer, error, { id }) => {
        if (error && error.response?.data?.element?.reason !== "NOT_DUE")
          return;
        queryClient.invalidateQueries(detailKey(entityId, id));
        invalidateAppointments(entityId);
      },
    }
  );

/**
 * Takes a payment alert off a person's profile, the payment itself untouched. A 404 means the alert is already gone:
 * the list showing it was out of date, and is read again as after a success.
 */
export const useMarkPaymentIssueHandled = (
  customerId: string,
  entityId: string,
  userId: string
) =>
  useMutation<{ id: string; handled_at: number }, AxiosError<any>, string>(
    async (id) => {
      const res = await HttpService.post(
        `${base(
          customerId,
          entityId
        )}/users/${userId}/payment-issues/${id}/handled`
      );
      return res?.data?.element;
    },
    {
      onSettled: (_answer, error) =>
        !error || error.response?.status === 404
          ? invalidateAppointments(entityId)
          : undefined,
    }
  );

/**
 * An appointment that answered 404 is gone: the lists showing it are read again. Its detail stays as it is, since
 * dropping it would make its open window read it again, and find it gone again, without end.
 */
export const forgetAppointment = (entityId: string) =>
  invalidateAppointments(entityId);

/** Reading an appointment settles a payment made since: the lists showing its former state are read again. */
export const refreshAppointmentLists = (entityId: string) =>
  invalidateAppointments(entityId);

/**
 * Reads the lists again once the earliest shown hold runs out: an appointment held while its client pays leaves the
 * agenda at that moment, as the server no longer lists it.
 */
export const useRefreshAtHoldExpiry = (
  entityId: string,
  appointments: Pick<AppointmentSummary, "status" | "hold_expires_at">[]
) => {
  const now = Date.now();
  const nextExpiry = appointments.reduce<number | null>((earliest, a) => {
    const at = a.status === "pending" ? a.hold_expires_at : null;
    return at && at > now && (earliest === null || at < earliest)
      ? at
      : earliest;
  }, null);
  useEffect(() => {
    if (nextExpiry === null) return;
    const id = window.setTimeout(
      () => invalidateAppointments(entityId),
      nextExpiry - Date.now() + 1000
    );
    return () => window.clearTimeout(id);
  }, [entityId, nextExpiry]);
};

export const useCreateAppointment = (customerId: string, entityId: string) =>
  useMutation<Appointment, AxiosError<any>, AppointmentInput>(
    async (input) => {
      const res = await HttpService.post(
        `${base(customerId, entityId)}/appointments`,
        input
      );
      return res?.data?.element;
    },
    { onSettled: () => invalidateAppointments(entityId) }
  );

export const useUpdateAppointment = (customerId: string, entityId: string) =>
  useMutation<
    Appointment,
    AxiosError<any>,
    { id: string; participant?: string; patch: Partial<AppointmentInput> }
  >(
    async ({ id, participant, patch }) => {
      const res = await HttpService.patch(
        `${base(customerId, entityId)}/appointments/${id}`,
        patch,
        { params: participant ? { participant } : {} }
      );
      return res?.data?.element;
    },
    {
      onSuccess: (_data, { id }) => invalidateAppointments(entityId, id),
      onError: (error, { id }) => afterFailedChange(entityId, id, error),
    }
  );

export const useDeleteAppointment = (customerId: string, entityId: string) =>
  useMutation<
    { id: string },
    AxiosError<any>,
    { id: string; participant?: string }
  >(
    async ({ id, participant }) => {
      const res = await HttpService.delete(
        `${base(customerId, entityId)}/appointments/${id}`,
        { params: participant ? { participant } : {} }
      );
      return res?.data?.element;
    },
    {
      onSuccess: (_data, { id }) => invalidateAppointments(entityId, id),
      onError: (error, { id }) => afterFailedChange(entityId, id, error),
    }
  );

/** Why the paid types cannot be booked: the plan has no payment links, or the Stripe payout account is not active. */
export type PaymentsBlocked = "plan" | "payout_account" | null;

interface AgendaTypesPage {
  types: AgendaType[];
  payments_blocked: PaymentsBlocked;
}

type TypesQueryOptions = {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean | "always";
};

const useAgendaTypesPage = <T,>(
  customerId: string,
  entityId: string,
  select: (page: AgendaTypesPage) => T,
  { enabled = true, refetchOnWindowFocus = true }: TypesQueryOptions = {}
) =>
  useQuery<AgendaTypesPage, AxiosError<any>, T>(
    typesKey(entityId),
    async () => {
      const res = await HttpService.get(`${base(customerId, entityId)}/types`);
      return {
        types: res?.data?.element?.types ?? [],
        payments_blocked: res?.data?.element?.payments_blocked ?? null,
      };
    },
    {
      enabled: enabled && !!customerId && !!entityId,
      staleTime: 60_000,
      refetchOnWindowFocus,
      select,
    }
  );

const typesOf = (page: AgendaTypesPage) => page.types;
const paymentsBlockedOf = (page: AgendaTypesPage) => page.payments_blocked;

export const useGetAgendaTypes = (
  customerId: string,
  entityId: string,
  options?: TypesQueryOptions
) => useAgendaTypesPage(customerId, entityId, typesOf, options);

export const useAgendaPaymentsBlocked = (
  customerId: string,
  entityId: string,
  options?: TypesQueryOptions
): PaymentsBlocked =>
  useAgendaTypesPage(customerId, entityId, paymentsBlockedOf, options).data ??
  null;

// The bookable people listed by the configuration are those the types offer.
const invalidateTypes = (entityId: string) =>
  Promise.all([
    queryClient.invalidateQueries(typesKey(entityId)),
    queryClient.invalidateQueries(configKey(entityId)),
  ]);

export const useCreateAgendaType = (customerId: string, entityId: string) =>
  useMutation<AgendaType, AxiosError<any>, AgendaTypeInput>(
    async (input) => {
      const res = await HttpService.post(
        `${base(customerId, entityId)}/types`,
        input
      );
      return res?.data?.element;
    },
    { onSettled: () => invalidateTypes(entityId) }
  );

export const useUpdateAgendaType = (customerId: string, entityId: string) =>
  useMutation<
    AgendaType,
    AxiosError<any>,
    { id: string; patch: Partial<AgendaTypeInput> }
  >(
    async ({ id, patch }) => {
      const res = await HttpService.patch(
        `${base(customerId, entityId)}/types/${id}`,
        patch
      );
      return res?.data?.element;
    },
    { onSettled: () => invalidateTypes(entityId) }
  );

/** Takes the order of every active type; the server answers the types already in it. */
export const useReorderAgendaTypes = (customerId: string, entityId: string) =>
  useMutation<AgendaType[], AxiosError<any>, string[]>(
    async (order) => {
      const res = await HttpService.put(
        `${base(customerId, entityId)}/types/order`,
        { order }
      );
      return res?.data?.element?.types ?? [];
    },
    {
      onSuccess: (types) => {
        queryClient.setQueryData<AgendaTypesPage>(
          typesKey(entityId),
          (page) => ({
            types,
            payments_blocked: page?.payments_blocked ?? null,
          })
        );
      },
      onError: () => queryClient.invalidateQueries(typesKey(entityId)),
    }
  );

export const useDeactivateAgendaType = (customerId: string, entityId: string) =>
  useMutation<{ id: string }, AxiosError<any>, string>(
    async (id) => {
      const res = await HttpService.delete(
        `${base(customerId, entityId)}/types/${id}`
      );
      return res?.data?.element;
    },
    { onSettled: () => invalidateTypes(entityId) }
  );
